import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import {
  Check,
  ClipboardCheck,
  HelpCircle,
  Loader2,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MathContent } from "@/components/math/math-content";
import { ChoiceProblem } from "@/components/math/choice-problem";
import { gradeLabel } from "@/lib/students";
import { cn } from "@/lib/utils";
import {
  ANSWER_PLACEMENT_TEST,
  START_PLACEMENT_TEST,
} from "@/graphql/mutations/students";

export type PlacementQuestion = {
  id: string;
  kind: string;
  skillKey: string;
  skillName: string | null;
  grade: number | null;
  level: number | null;
  html: string | null;
  hint: string | null;
  choices: string[];
};

export type PlacementGradeRow = {
  grade: number;
  probed: number;
  claimed: number;
  checked: number;
  correct: number;
  credited: boolean;
};

export type PlacementTest = {
  id: string;
  subject: string;
  subjectName: string | null;
  grade: number | null;
  stage: string;
  totalCount: number;
  answeredCount: number;
  completedAt: string | null;
  stale: boolean;
  knownCeiling: number | null;
  claimedCount: number;
  checkedCount: number;
  correctCount: number;
  gradeRows: PlacementGradeRow[];
  question: PlacementQuestion | null;
};

/** What a learner may say about a skill. The words are the feature: „Nemoku" is
    a statement about the topic, where „nežinau" would be one about themselves —
    the same distinction `DiscussionTutor::DONT_KNOW` is built on. */
const CLAIMS = [
  { value: "yes", label: "Moku", icon: ThumbsUp, tone: "emerald" },
  { value: "unsure", label: "Šiek tiek", icon: HelpCircle, tone: "amber" },
  { value: "no", label: "Nemoku", icon: ThumbsDown, tone: "slate" },
] as const;

const TONE: Record<string, string> = {
  emerald:
    "border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-200 dark:hover:bg-emerald-950/40",
  amber:
    "border-amber-300 text-amber-800 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-200 dark:hover:bg-amber-950/40",
  slate: "border-border text-muted-foreground hover:bg-muted/60",
};

/**
 * „Patikrinkime, ką jau moka" — the short test that turns the setup screen's
 * guess into a measurement.
 *
 * The wizard's offer is derived from a birth year, which describes a classroom
 * rather than a child. This asks instead: a run of „ar moki šią temą?" across
 * the grades up to their own, then a real question out of each topic they
 * claimed — because a claim and a skill are different things, and „known" is the
 * stickiest verdict anybody can write about a learner.
 *
 * **Nothing here writes anything.** A finished test makes the proposal above it
 * better; „Pritaikyti" is still what changes a single row, and every change is
 * still shown first.
 */
export function PlacementTestPanel({
  studentId,
  name,
  subjects,
  tests,
  onFinished,
}: {
  studentId: string;
  name: string;
  /** The subjects the proposal covers, in the order it shows them. */
  subjects: { id: string; name: string }[];
  tests: PlacementTest[];
  /** A test just completed — the proposal above is now built from it. */
  onFinished: () => void;
}) {
  const [subject, setSubject] = useState<string | null>(null);
  // The test being answered right now. Kept locally rather than refetched per
  // answer: the mutation hands back the whole test with its next question on
  // it, so a round trip to the list would be a second request saying the same
  // thing — and a flicker between the two.
  const [live, setLive] = useState<PlacementTest | null>(null);
  const [typed, setTyped] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const active = subject ?? subjects[0]?.id ?? null;
  const stored = useMemo(
    () => tests.find((test) => test.subject === active) ?? null,
    [tests, active]
  );
  const test = live?.subject === active ? live : stored;
  const question = test?.question ?? null;

  // A fresh question is a fresh answer. Carrying the last one across would put
  // somebody's answer to question 4 into question 5's box.
  useEffect(() => setTyped(""), [question?.id]);
  useEffect(() => {
    if (question?.kind === "check") inputRef.current?.focus();
  }, [question?.id, question?.kind]);

  const [start, { loading: starting }] = useMutation(START_PLACEMENT_TEST, {
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });
  const [answer, { loading: answering }] = useMutation(ANSWER_PLACEMENT_TEST, {
    onError: (error) => toast.error(`Klaida: ${error.message}`),
  });

  const busy = starting || answering;

  const begin = () => {
    if (!active) return;
    start({
      variables: { studentId, subject: active },
      onCompleted: (result) => {
        const errors: string[] = result.startPlacementTest.errors ?? [];
        if (errors.length > 0) {
          toast.error(errors.join(", "));
          return;
        }
        setLive(result.startPlacementTest.test);
      },
    });
  };

  const send = (value: string) => {
    if (!test || !question || !value.trim()) return;
    answer({
      variables: { testId: test.id, questionId: question.id, answer: value },
      onCompleted: (result) => {
        const errors: string[] = result.answerPlacementTest.errors ?? [];
        if (errors.length > 0) {
          toast.error(errors.join(", "));
          return;
        }
        const next: PlacementTest | null = result.answerPlacementTest.test;
        setLive(next);
        // The proposal above is derived from the finished test, so the moment
        // the last answer lands it is out of date. Refetched here rather than
        // on every answer: an unfinished test feeds nothing.
        if (next?.stage === "done") onFinished();
      },
    });
  };

  return (
    <div className="rounded-lg border bg-muted/20">
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <ClipboardCheck className="h-4 w-4 shrink-0 text-primary" />
        <span className="text-sm font-medium">Patikrinkime, ką {name} jau moka</span>
        {subjects.length > 1 && (
          <div className="ml-auto flex items-center gap-1">
            {subjects.map((entry) => (
              <Button
                key={entry.id}
                size="sm"
                variant={entry.id === active ? "secondary" : "ghost"}
                className="h-7 text-xs"
                onClick={() => {
                  setSubject(entry.id);
                  setLive(null);
                }}
                disabled={busy}
              >
                {entry.name}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3 p-3">
        {!test ? (
          <Offer onStart={begin} starting={starting} />
        ) : question ? (
          <>
            <Progress test={test} />
            {question.kind === "probe" ? (
              <ProbeQuestion question={question} onAnswer={send} disabled={busy} />
            ) : (
              <CheckQuestion
                question={question}
                typed={typed}
                setTyped={setTyped}
                onAnswer={send}
                disabled={busy}
                inputRef={inputRef}
              />
            )}
            {answering && (
              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                Įrašoma…
              </p>
            )}
          </>
        ) : (
          <Finding test={test} onRestart={begin} starting={starting} />
        )}
      </div>
    </div>
  );
}

function Offer({ onStart, starting }: { onStart: () => void; starting: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <p className="min-w-0 flex-1 text-xs text-muted-foreground">
        Kelios „ar moki šią temą?" ir kelios tikros užduotys iš tų temų, kurias
        pasakys mokantis — kad „moka" reikštų mokėjimą, o ne „esu girdėjęs".
        Užtruks porą minučių ir nieko neįrašys: tik patikslins pasiūlymą apačioje.
      </p>
      <Button size="sm" onClick={onStart} disabled={starting} className="shrink-0">
        {starting && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
        Pradėti patikrą
      </Button>
    </div>
  );
}

function Progress({ test }: { test: PlacementTest }) {
  const done = test.answeredCount;
  const total = Math.max(test.totalCount, done + 1);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${Math.round((done / total) * 100)}%` }}
        />
      </div>
      <span className="shrink-0 text-[11px] text-muted-foreground">
        {/* The second phase's length depends on the first phase's answers, so
            the total grows. „iš 8" that later becomes „iš 12" reads as a
            mistake; „8 klausimas" never does. */}
        {done + 1} klausimas
        {test.stage === "verify" && " · patikrinam"}
      </span>
    </div>
  );
}

function ProbeQuestion({
  question,
  onAnswer,
  disabled,
}: {
  question: PlacementQuestion;
  onAnswer: (value: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {question.grade != null && gradeLabel(question.grade)}
      </p>
      <p className="text-base font-medium">{question.skillName}</p>
      {question.hint && (
        <p className="text-xs text-muted-foreground">{question.hint}</p>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        {CLAIMS.map((claim) => (
          <Button
            key={claim.value}
            variant="outline"
            size="sm"
            className={cn("gap-1.5", TONE[claim.tone])}
            onClick={() => onAnswer(claim.value)}
            disabled={disabled}
          >
            <claim.icon className="h-3.5 w-3.5" />
            {claim.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function CheckQuestion({
  question,
  typed,
  setTyped,
  onAnswer,
  disabled,
  inputRef,
}: {
  question: PlacementQuestion;
  typed: string;
  setTyped: (value: string) => void;
  onAnswer: (value: string) => void;
  disabled: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
}) {
  // A question whose file has moved since the test was planned. It cannot be
  // skipped silently — the plan is stored, so the test would stall on it — so it
  // says so and offers the way past.
  if (!question.html) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground">
          Šios užduoties nebepavyksta atverti.
        </p>
        <Button size="sm" variant="outline" onClick={() => onAnswer("—")} disabled={disabled}>
          Praleisti
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {question.skillName}
        {question.grade != null && ` · ${gradeLabel(question.grade)}`}
      </p>
      {question.choices.length > 0 ? (
        <ChoiceProblem
          questionHtml={question.html}
          choices={question.choices}
          onSelectAnswer={(value) => value && onAnswer(value)}
          disabled={disabled}
          zoomableFigures
        />
      ) : (
        <>
          <MathContent html={question.html} zoomableFigures />
          <form
            className="flex items-center gap-2 pt-1"
            onSubmit={(event) => {
              event.preventDefault();
              onAnswer(typed);
            }}
          >
            <Input
              ref={inputRef}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              placeholder="Atsakymas"
              className="max-w-[12rem]"
              disabled={disabled}
            />
            <Button type="submit" size="sm" disabled={disabled || !typed.trim()}>
              Toliau
            </Button>
          </form>
        </>
      )}
    </div>
  );
}

/**
 * What the test found, in the terms it found it in.
 *
 * On screen next to the proposal rather than folded into it, because the two say
 * different things: „4 klasė — sakė mokanti 2 iš 2, patikra 1 iš 2" is the
 * sentence that explains why the offer stops where it does, and an offer whose
 * reasoning is invisible is one a grown-up can only accept or ignore.
 */
function Finding({
  test,
  onRestart,
  starting,
}: {
  test: PlacementTest;
  onRestart: () => void;
  starting: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="gap-1 font-normal">
          <Check className="h-3 w-3" />
          Patikra baigta
        </Badge>
        {test.knownCeiling != null ? (
          <span className="text-xs text-muted-foreground">
            Iki {gradeLabel(test.knownCeiling)} — atsakymai laikosi.
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            Pagal atsakymus nė vienos klasės dar nelaikome išmokta.
          </span>
        )}
        {test.stale && (
          <Badge variant="outline" className="gap-1 font-normal text-amber-700 dark:text-amber-300">
            Atlikta kitoje klasėje
          </Badge>
        )}
      </div>

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-muted-foreground">
            <tr>
              <th className="px-2 py-1 text-left font-medium">Klasė</th>
              <th className="px-2 py-1 text-right font-medium">Sakė mokanti</th>
              <th className="px-2 py-1 text-right font-medium">Patikra</th>
              <th className="px-2 py-1 text-right font-medium"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {test.gradeRows.map((row) => (
              <tr key={row.grade}>
                <td className="px-2 py-1">{gradeLabel(row.grade)}</td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {row.claimed} / {row.probed}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">
                  {row.checked === 0 ? "—" : `${row.correct} / ${row.checked}`}
                </td>
                <td className="px-2 py-1 text-right">
                  {row.credited ? (
                    <Check className="ml-auto h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                  ) : (
                    <X className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <p className="min-w-0 flex-1 text-xs text-muted-foreground">
          Pasiūlymas apačioje jau perskaitytas iš patikros. Nieko neįrašyta, kol
          nepaspausi „Pritaikyti".
        </p>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 shrink-0 text-xs"
          onClick={onRestart}
          disabled={starting}
        >
          {starting ? (
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
          ) : (
            <RefreshCw className="mr-1.5 h-3 w-3" />
          )}
          Perklausti
        </Button>
      </div>
    </div>
  );
}
